import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Loader2 } from 'lucide-react';
import ClassForm from '@/components/ClassForm';
import { Class } from '@/types';
import { classApi } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';

const EditClassPage = () => {
  const { classId } = useParams<{ classId: string }>();
  const [classData, setClassData] = useState<Class | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchClass = async () => {
      try {
        setLoading(true);
        if (classId) {
          const data = await classApi.getById(classId);
          setClassData(data);
        }
      } catch (error) {
        console.error('Error fetching class:', error);
        toast({
          title: 'Error',
          description: 'Failed to load class data. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchClass();
  }, [classId, toast]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link to="/classes">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Edit Class</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Class Information</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : classData ? (
            <ClassForm classData={classData} isEditing={true} />
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Class not found.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EditClassPage;
